import classNames from 'classnames'
import IconRender from 'components/common/IconRender'
import { isMobile } from 'components/utils'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CustomColorsRef, IconSizes } from 'types/general'

interface Tag {
  id: string
  label: string
  icon?: string
  category?: string
}

interface FilterTagsProps {
  handleChange: (key: string, value: any) => void
  selectedTags: string[]
  tags: Tag[]
  onFilter?: () => void
}

const FilterTags = ({ handleChange, selectedTags, tags, onFilter }: FilterTagsProps) => {
  const mobile = isMobile()

  const [isOpen, setIsOpen] = useState(false)
  const [dropdownWidth, setDropdownWidth] = useState(320)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const selectedCount = selectedTags.length
  const totalCount = tags.length
  const { t } = useTranslation();

  const handleTagToggle = (tagId: string) => {
    const newSelectedTags = selectedTags.includes(tagId)
      ? selectedTags.filter(id => id !== tagId)
      : [...selectedTags, tagId]

    handleChange('tags', newSelectedTags)
  }

  const handleClear = () => {
    handleChange('tags', [])
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const buttonWidth = buttonRef.current.getBoundingClientRect().width
      setDropdownWidth(buttonWidth * 4)
    }
  }, [isOpen])

  const groupedTags = tags.reduce(
    (acc, tag) => {
      const category = tag.category || 'Other'
      if (!acc[category]) {
        acc[category] = []
      }
      acc[category].push(tag)
      return acc
    },
    {} as Record<string, Tag[]>
  )

  return (
    <div className="relative text-sm w-full sm:w-fit" ref={dropdownRef}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={classNames('w-full flex items-center relative pl-10 pr-8', {
          'h-10 rounded-lg justify-center': !mobile,
          'h-12 ': mobile,
          'border border-imovPrimary': isOpen && !mobile,
          'border border-gray-300': !isOpen && !mobile,
          'border-b border-gray-300': !isOpen && mobile,
        })}
      >
        <span className="absolute left-3 inset-y-0 flex items-center text-gray-500">
          <IconRender
            icon="regular-tags"
            size={IconSizes.LG}
            color={CustomColorsRef.Primary}
          />
        </span>
        <span className="text-imovPrimary px-2 text-md font-semibold whitespace-nowrap flex">{t('filters.tags.label')}
          <div className="min-w-14 pl-1">
            ({selectedTags.length}/{totalCount})
          </div>
        </span>
        <span className={classNames("absolute inset-y-0 flex items-center text-gray-500", {
          "right-2": !mobile,
          "right-10": mobile,
        })}>
          <IconRender
            icon={isOpen ? 'regular-chevron-up' : 'regular-chevron-down'}
            size={IconSizes.LG}
            color={CustomColorsRef.Primary}
          />
        </span>
      </button>
      {(isOpen || mobile) && buttonRef.current && (
        <div
          className={classNames(
            "z-50 bg-white border border-gray-200 text-sm transition-all duration-300 ease-in-out",
            {
              "fixed rounded-md shadow-lg": !mobile,
              "relative": mobile,
              "opacity-0 max-h-0 overflow-hidden": mobile && !isOpen,
              "opacity-100 max-h-96 overflow-visible": mobile && isOpen,
            }
          )}
          style={!mobile ? {
            top: 50,
            width: dropdownWidth,
            transform: 'translateX(-30%)',
          } : {}}
        >
          {!mobile && selectedCount > 0 && (
            <div className="flex gap-2 p-2 shadow-sm">
              <button onClick={() => onFilter?.()} className="flex-1 px-3 py-2 bg-imovPrimary text-white rounded cursor-pointer hover:bg-imovPrimaryLight transition-colors">
                {t('common.filter')}
              </button>
              <button onClick={() => handleClear()} className="flex-1 px-3 py-2 bg-gray-200 text-gray-700 rounded cursor-pointer hover:bg-gray-300 transition-colors">
                {t('common.clear')}
              </button>
            </div>
          )}

          <div className="p-4 max-h-[40vh] overflow-y-auto">
            <div className="space-y-4">
              {Object.entries(groupedTags).map(([category, tags]) => (
                <div key={category}>
                  <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 border-b border-gray-200 pb-2">
                    {t(category)}
                  </h4>
                  <div className="grid grid-cols-3 gap-1">
                    {tags.map(tag => (
                      <button
                        key={tag.id}
                        onClick={() => handleTagToggle(tag.id)}
                        className={`flex items-center space-x-2 p-2 rounded cursor-pointer transition-all duration-200 w-full text-left ${selectedTags.includes(tag.id)
                          ? 'border-2 border-imovPrimary bg-imovPrimary bg-opacity-5'
                          : 'border-2 border-transparent hover:bg-gray-50'
                          }`}
                      >
                        <div className="flex items-center space-x-2 flex-1">
                          {tag.icon && (
                            <IconRender
                              icon={tag.icon}
                              size={IconSizes.SM}
                              color={
                                selectedTags.includes(tag.id)
                                  ? CustomColorsRef.Primary
                                  : CustomColorsRef.Gray
                              }
                            />
                          )}
                          <span
                            className={`text-sm ${selectedTags.includes(tag.id)
                              ? 'text-imovPrimary font-medium'
                              : 'text-gray-700'
                              }`}
                          >
                            {t(tag.label)}
                          </span>
                        </div>
                        {selectedTags.includes(tag.id) && (
                          <IconRender
                            icon="regular-check"
                            size={IconSizes.SM}
                            color={CustomColorsRef.Primary}
                          />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default FilterTags
